import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import LockIcon from "@mui/icons-material/Lock";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "react-toastify";

import SettingsPanel from "../../components/SettingsPanel";
import handleDateTime from "../../../../Components/Common/HandleDateTime";
import {
  developerAccessListApiCall,
  developerAccessSearchApiCall,
  developerAccessUpdateApiCall,
} from "../../../../helpers/fakebackend_helper";

/**
 * Super Admin > Users & Access > Developers (CRMit Task 24434).
 *
 * The one screen that writes User.FlagDeveloper - the flag behind Chat CRMit,
 * the "Let AI handle this" checkbox on Report a Bug, the Database Deltas tab,
 * the Twilio dialer and the mobile Explore tab. Before this it could only be
 * set by an UPDATE against ZCRM_Master by hand.
 *
 * Only the nominated account may see or use it. The same id is enforced in
 * DeveloperAccessController, which is the check that actually matters - this
 * one just keeps the screen out of everybody else's way.
 *
 * Search-first by design: FlagDeveloper is platform-wide, so the pool is every
 * User row across every company rather than the current company's team.
 */

/** Must match DeveloperAccessController::ALLOWED_USER_ID. */
const ALLOWED_DEVELOPER_FLAG_USER_ID = 45;

const SEARCH_MIN_LENGTH = 2;

const isDeveloper = (row) => Number(row?.User_FlagDeveloper) === 1;

export default function Developers() {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  // Staff.Id == User.Id, so either shape answers "who am I".
  const currentUserId = Number(
    authUser?.get_user_data?.User_Id ?? authUser?.get_staff_data?.Staff_Id ?? 0
  );
  const allowed = currentUserId === ALLOWED_DEVELOPER_FLAG_USER_ID;

  const [developerList, setDeveloperList] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  // The row whose flag is being confirmed, plus the value we would write.
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await developerAccessListApiCall();
      setDeveloperList(
        Number(res?.status) === 200 ? res?.developeraccess_list || [] : []
      );
      if (Number(res?.status) !== 200) {
        toast.error(res?.message || "Could not load the developer list");
      }
    } catch (e) {
      toast.error("Could not load the developer list");
    }
    setListLoading(false);
  }, []);

  useEffect(() => {
    if (!allowed) return;
    loadList();
  }, [allowed, loadList]);

  const runSearch = async () => {
    const term = searchTerm.trim();
    if (term.length < SEARCH_MIN_LENGTH) {
      toast.error(`Enter at least ${SEARCH_MIN_LENGTH} characters to search`);
      return;
    }
    setSearchLoading(true);
    try {
      const res = await developerAccessSearchApiCall([{ SearchTerm: term }]);
      if (Number(res?.status) === 200) {
        setSearchResults(res?.developeraccessuser_list || []);
      } else {
        setSearchResults([]);
        toast.error(res?.message || "Could not search users");
      }
      setSearched(true);
    } catch (e) {
      toast.error("Could not search users");
    }
    setSearchLoading(false);
  };

  const handleConfirmSave = async () => {
    if (!confirmTarget) return;
    const { row, nextFlag } = confirmTarget;
    setSaving(true);
    try {
      const res = await developerAccessUpdateApiCall([
        { User_Id: row?.User_Id, FlagDeveloper: nextFlag },
      ]);
      if (Number(res?.status) === 200) {
        toast.success(res?.message || "Updated");
        // Keep the search grid honest without making them search again.
        setSearchResults((prev) =>
          prev.map((item) =>
            String(item?.User_Id) === String(row?.User_Id)
              ? { ...item, User_FlagDeveloper: nextFlag }
              : item
          )
        );
        loadList();
        setConfirmTarget(null);
      } else {
        toast.error(res?.message || "Could not change developer access");
      }
    } catch (e) {
      toast.error("Could not change developer access");
    }
    setSaving(false);
  };

  // One toggle button, used in both grids.
  const renderAccessButton = (row) => {
    const on = isDeveloper(row);
    const isSelf = Number(row?.User_Id) === currentUserId;
    const disabled = isSelf && on;
    const button = (
      <Button
        size="small"
        variant={on ? "outlined" : "contained"}
        color={on ? "error" : "success"}
        disabled={disabled}
        onClick={() => setConfirmTarget({ row, nextFlag: on ? 0 : 1 })}
      >
        {on ? "Revoke" : "Grant"}
      </Button>
    );
    return disabled ? (
      <Tooltip title="You cannot remove your own developer access - it is what gives you this screen">
        <span>{button}</span>
      </Tooltip>
    ) : (
      button
    );
  };

  const baseColumns = useMemo(
    () => [
      { field: "User_Id", headerName: "Id", width: 90 },
      { field: "User_UserFullName", headerName: "Name", flex: 1, minWidth: 180 },
      { field: "User_Email", headerName: "Email", flex: 1, minWidth: 220 },
      {
        field: "User_LastLoginDateTime",
        headerName: "Last Login",
        width: 180,
        renderCell: (params) =>
          params.value ? handleDateTime(params.value) : "Never",
      },
    ],
    []
  );

  const listColumns = useMemo(
    () => [
      ...baseColumns,
      {
        field: "User_FlagDeveloperUpdatedDateTime",
        headerName: "Granted",
        width: 180,
        renderCell: (params) =>
          params.value ? handleDateTime(params.value) : "-",
      },
      {
        field: "User_FlagDeveloperUpdatedByUserId",
        headerName: "By",
        width: 90,
        renderCell: (params) => (params.value ? `#${params.value}` : "-"),
      },
      {
        field: "actions",
        headerName: "Access",
        width: 130,
        sortable: false,
        renderCell: (params) => renderAccessButton(params.row),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseColumns, currentUserId]
  );

  const searchColumns = useMemo(
    () => [
      ...baseColumns,
      {
        field: "User_FlagDeveloper",
        headerName: "Developer",
        width: 130,
        renderCell: (params) =>
          isDeveloper(params.row) ? (
            <Chip label="Yes" color="success" size="small" />
          ) : (
            <Chip label="No" size="small" variant="outlined" />
          ),
      },
      {
        field: "actions",
        headerName: "Access",
        width: 130,
        sortable: false,
        renderCell: (params) => renderAccessButton(params.row),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseColumns, currentUserId]
  );

  if (!allowed) {
    return (
      <SettingsPanel
        title="Developers"
        description="Who holds the developer flag."
      >
        <Box sx={{ p: 3 }}>
          <Alert severity="info" icon={<LockIcon fontSize="inherit" />}>
            <AlertTitle>Restricted area</AlertTitle>
            Developer access is managed by user #{ALLOWED_DEVELOPER_FLAG_USER_ID}{" "}
            and nobody else. You are signed in as user #{currentUserId || "?"}.
          </Alert>
        </Box>
      </SettingsPanel>
    );
  }

  return (
    <SettingsPanel
      title="Developers"
      description={`Restricted to user #${ALLOWED_DEVELOPER_FLAG_USER_ID} only. Who holds User.FlagDeveloper - the flag behind Chat CRMit, the Let AI handle this checkbox on Report a Bug, the Database Deltas tab, the Twilio dialer and the mobile Explore tab. It is platform-wide, so the search covers every user on every company, not just this one.`}
    >
      <Box sx={{ p: 3, width: "100%", minWidth: 0, maxWidth: "100%" }}>
        {/* Say out loud who this screen belongs to. A restriction nobody can
            see looks like a screen that is simply missing for everyone else. */}
        <Alert
          severity="info"
          icon={<LockIcon fontSize="inherit" />}
          sx={{ mb: 2 }}
        >
          <AlertTitle>Restricted to user #{ALLOWED_DEVELOPER_FLAG_USER_ID}</AlertTitle>
          You are signed in as user #{currentUserId}, the one account that may
          see or change developer access. It is not a role or a capability:
          nobody else gets this tab, and every /developeraccess/ endpoint
          refuses them as well, so the restriction holds outside the UI too.
        </Alert>

        <Alert severity="warning" sx={{ mb: 3 }}>
          The flag is read into the login token, so a change only reaches
          someone after they sign out and back in.
        </Alert>

        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h5" component="h1" fontWeight="bold">
              Current Developers
            </Typography>
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              icon={<LockIcon />}
              label={`Only user #${ALLOWED_DEVELOPER_FLAG_USER_ID} can edit`}
            />
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadList}
          >
            Refresh
          </Button>
        </Box>

        <Paper elevation={3} sx={{ height: 380, width: "100%", mb: 4 }}>
          <DataGrid
            rows={developerList}
            columns={listColumns}
            getRowId={(row) => row.User_Id}
            loading={listLoading}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            disableRowSelectionOnClick
            sx={{ border: 0 }}
          />
        </Paper>

        <Typography
          variant="h5"
          component="h2"
          fontWeight="bold"
          sx={{ mb: 2 }}
        >
          Grant Access
        </Typography>

        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <TextField
            size="small"
            label="Search by name, email or user id"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") runSearch();
            }}
            sx={{ width: 420, maxWidth: "100%" }}
          />
          <Button
            variant="contained"
            startIcon={
              searchLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SearchIcon />
              )
            }
            disabled={searchLoading}
            onClick={runSearch}
          >
            Search
          </Button>
        </Box>

        {searched && !searchLoading && searchResults.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No users matched that search.
          </Alert>
        )}

        {searchResults.length > 0 && (
          <Paper elevation={3} sx={{ height: 460, width: "100%" }}>
            <DataGrid
              rows={searchResults}
              columns={searchColumns}
              getRowId={(row) => row.User_Id}
              loading={searchLoading}
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              disableRowSelectionOnClick
              sx={{ border: 0 }}
            />
          </Paper>
        )}

        <Dialog
          open={Boolean(confirmTarget)}
          onClose={() => (saving ? null : setConfirmTarget(null))}
        >
          <DialogTitle>
            {confirmTarget?.nextFlag === 1
              ? "Grant developer access?"
              : "Revoke developer access?"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {confirmTarget?.nextFlag === 1 ? (
                <>
                  {confirmTarget?.row?.User_UserFullName || "This user"} (
                  {confirmTarget?.row?.User_Email}) will get developer tooling
                  across every company they can sign in to, including the
                  Database Deltas screen. They must sign out and back in for it
                  to take effect.
                </>
              ) : (
                <>
                  {confirmTarget?.row?.User_UserFullName || "This user"} (
                  {confirmTarget?.row?.User_Email}) will lose developer tooling
                  the next time they sign in.
                </>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button disabled={saving} onClick={() => setConfirmTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color={confirmTarget?.nextFlag === 1 ? "success" : "error"}
              disabled={saving}
              onClick={handleConfirmSave}
            >
              {saving
                ? "Saving..."
                : confirmTarget?.nextFlag === 1
                ? "Grant"
                : "Revoke"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </SettingsPanel>
  );
}
